import { IconButton } from '@atlaskit/button/new';
import LinkIcon from '@atlaskit/icon/core/migration/link';
import Tooltip from '@atlaskit/tooltip';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { messages as defaultMessages } from '@databases/intl/messages';
import { showErrorSnackbar } from '@databases/utils/errors';
import React, { FunctionComponent, useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';

import useCopyViewLink from '../../hooks/useCopyViewLink';
import useView from '../../hooks/useView';
import { messages } from '../../i18n';

interface CopyLinkButtonProps {
  contentId: string;
}

const CopyLinkButton: FunctionComponent<CopyLinkButtonProps> = ({ contentId }) => {
  const { formatMessage } = useIntl();
  const client = useAnalytics();
  const { view } = useView();
  const [isCopied, setIsCopied] = useState(false);

  const onCopyLink = useCopyViewLink(contentId);

  const handleCopyLink = useCallback(async () => {
    const result = await onCopyLink(view);

    if (result) {
      setIsCopied(true);

      client.sendViewLinkCopiedEvent({
        contentId,
        databaseContainer: getDatabaseContainerType(),
        source: 'databaseViewSettingsPanel',
        viewId: view.id,
      });

      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
    } else {
      showErrorSnackbar(
        'failed-to-copy-error',
        formatMessage(messages['view-actions.failed-to-copy-error'])
      );
    }
  }, [client, contentId, formatMessage, onCopyLink, view]);

  return (
    <Tooltip
      content={
        isCopied
          ? formatMessage(defaultMessages['copy-link.tooltip'])
          : formatMessage(messages['view-settings.copy-link'])
      }
      testId="view-settings-panel-copy-link-tooltip"
    >
      {(tooltipProps) => (
        <IconButton
          {...tooltipProps}
          appearance="subtle"
          icon={LinkIcon}
          label={formatMessage(messages['view-settings.copy-link'])}
          onClick={handleCopyLink}
          testId={'view-settings-panel-copy-link-button'}
        />
      )}
    </Tooltip>
  );
};

export default CopyLinkButton;
